import { RecordCollaborationPanel } from "../../../../collaboration/RecordCollaborationPanel";
import { normalizeError } from "../../../../operationFeedback";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  PencilIcon,
  InformationCircleIcon,
  ClipboardDocumentListIcon,
  TagIcon,
  UsersIcon,
  Cog6ToothIcon,
  XMarkIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CheckIcon,
  EllipsisHorizontalIcon,
  DocumentIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import "./report-edit-drawer.css";
import { fetchReportTypes } from "../../../../api/service/settings";
import { updateReportAsync } from "../../../../slices/service/reports";
import { QuillEditor } from "../../../utils/quill-editor";
import { ReportTypeSelector } from "../../../utils/moduleSelectors/reportTypeSelector";
import { EmployeeSelector } from "../../../utils/moduleSelectors/employeeSelector";
import { ContactPicker, CustomerPicker, DeliveryAddressPicker, ProjectFolderPicker, ProjectPicker, SystemPicker } from "../../../utils/pickers";
import { TemplateDialogButton } from "../../templates/template-dialog-button";
import { DocumentUploadSectionV5 } from "../../Media/DocumentUploadSectionV5";
import DocumentsDialog from "../../documents/documents-dialog";
import ChecklistItemLines from "../../quality/checklists/checklistItemLines";
import { ReportPriceLinesSection } from "./ReportPriceLinesSection";
import { ReportAccessesSection } from "./ReportAccessesSection";
import { EnhancedChecklistSelector } from "./enhanced-checklist-selector";
import {
  createReportChecklist,
  fetchReportChecklist,
  deleteReportChecklist,
} from "../../../../api/service/report";
import SimplePrompt from "../../../utils/simplePrompt";
import { useUserHasAccess } from "../../../../routes/roleOnly";

/**
 * ReportEditDrawer — the "Rediger informasjon" slide-over (report-view-v2).
 *
 * A pixel-exact recreation of the Claude Design prototype (Proma Report.html):
 * a right-hand drawer with five section tabs (Informasjon, Sjekkliste,
 * Prislinjer, Ansvarlige, Status), replacing the old tabbed edit dialog.
 *
 * Each section is wired to the real backend — Informasjon and Status save via
 * `updateReportAsync`, while Sjekkliste, Prislinjer and Ansvarlige embed the
 * production editors (which persist through their dedicated endpoints).
 */

/**
 * Stable signature of the meaningful checklist item fields. Used to detect real
 * edits vs. the harmless echo the embedded editor emits on mount (it tags rows
 * with a random `draggableId`, so a raw comparison would always look "dirty").
 */
function checklistItemsSignature(list) {
  return JSON.stringify(
    (list || []).map((it) => ({
      id: it?.id ?? null,
      title: it?.title ?? "",
      description: it?.description ?? "",
      row_order: it?.row_order ?? null,
      isApproved: !!it?.isApproved,
      isDeviation: !!it?.isDeviation,
      is_not_applicable: !!it?.is_not_applicable,
      response_code: it?.response_code ?? null,
      delete: !!it?.delete,
    }))
  );
}

export const REPORT_EDIT_SECTIONS = [
  {
    id: "info",
    icon: InformationCircleIcon,
    label: "report.edit.section.info",
    desc: "report.edit.section.info_desc",
  },
  {
    id: "checklist",
    icon: ClipboardDocumentListIcon,
    label: "report.edit.section.checklist",
    desc: "report.edit.section.checklist_desc",
  },
  {
    id: "price",
    icon: TagIcon,
    label: "report.edit.section.price",
    desc: "report.edit.section.price_desc",
  },
  {
    id: "team",
    icon: UsersIcon,
    label: "report.edit.section.team",
    desc: "report.edit.section.team_desc",
  },
  {
    id: "status",
    icon: Cog6ToothIcon,
    label: "report.edit.section.status",
    desc: "report.edit.section.status_desc",
  },
];

/* ----------------------------------------------------------------------- */
/* Header dropdown trigger: "Rediger informasjon ▾"                         */
/* ----------------------------------------------------------------------- */
export function ReportEditMenu({ onSelect }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <div className="rv2-scope" ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        className="rv2-edit-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <PencilIcon aria-hidden />
        {t("report.edit.menu_title")}
        <ChevronDownIcon className="rv2-caret" aria-hidden />
      </button>
      {open && (
        <div className="rv2-menu-pop" role="menu">
          <div className="rv2-mh">{t("report.edit.menu_title")}</div>
          {REPORT_EDIT_SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              className="rv2-menu-item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSelect(s.id);
              }}
            >
              <span className="rv2-mii">
                <s.icon aria-hidden />
              </span>
              <span className="rv2-mtext">
                <div>{t(s.label)}</div>
                <div className="rv2-mt-sub">{t(s.desc)}</div>
              </span>
              <ChevronRightIcon className="rv2-chev" width={15} aria-hidden />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Mobile stacked floating buttons: "Meny" over "Rediger informasjon"       */
/* ----------------------------------------------------------------------- */
export function ReportEditFabs({ onEdit, actions = [] }) {
  const { t } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div className="rv2-scope">
      {sheetOpen && (
        <>
          <div
            className="rv2-sheet-scrim"
            onClick={() => setSheetOpen(false)}
            aria-hidden
          />
          <div className="rv2-sheet" role="menu">
            <div className="rv2-mh">{t("service.reports.edit.actions")}</div>
            {actions.map((a, i) =>
              a.separator ? (
                <div key={`sep-${i}`} className="rv2-menu-sep" />
              ) : (
                <button
                  key={a.label || i}
                  type="button"
                  className="rv2-menu-item"
                  role="menuitem"
                  onClick={() => {
                    setSheetOpen(false);
                    a.action?.();
                  }}
                >
                  {a.icon && (
                    <span className="rv2-mii">
                      <a.icon aria-hidden />
                    </span>
                  )}
                  <span className="rv2-mtext">
                    <div>{a.label}</div>
                  </span>
                </button>
              )
            )}
          </div>
        </>
      )}
      <div className="rv2-fab-stack">
        <button
          type="button"
          className={"rv2-fab rv2-fab-menu" + (sheetOpen ? " rv2-active" : "")}
          onClick={() => setSheetOpen((o) => !o)}
          aria-label={t("common.menu")}
        >
          <EllipsisHorizontalIcon aria-hidden />
        </button>
        <button
          type="button"
          className="rv2-fab rv2-fab-edit"
          onClick={onEdit}
          aria-label={t("report.edit.menu_title")}
        >
          <PencilIcon aria-hidden />
        </button>
      </div>
    </div>,
    document.body
  );
}

/* ----------------------------------------------------------------------- */
/* Informasjon — wired to updateReportAsync                                 */
/* ----------------------------------------------------------------------- */
function InfoEditor({ report, onRefresh, onClose, registerFooter }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const blockedRef = useRef(false);
  const [customer, setCustomer] = useState(report?.customer || null);
  const [project, setProject] = useState(report?.project || null);
  const [contact, setContact] = useState(report?.contact || null);
  const [folder, setFolder] = useState(report?.folder || null);
  const [system, setSystem] = useState(report?.system || null);
  const [address, setAddress] = useState(report?.delivery_address || null);
  const [invoiceCustomer, setInvoiceCustomer] = useState(report?.invoice_customer || null);
  const [reportTypes, setReportTypes] = useState(() => (
    report?.report_type?.id != null ? [report.report_type] : []
  ));

  useEffect(() => {
    let current = true;
    fetchReportTypes({})
      .then((response) => {
        if (current && Array.isArray(response?.data)) setReportTypes(response.data);
      })
      .catch(() => undefined);
    return () => { current = false; };
  }, []);

  const initialValues = useMemo(
    () => ({
      id: report?.id,
      edit_version: report?.edit_version,
      report_type: report?.report_type?.id || null,
      executing_employee: report?.executing_employee?.id || null,
      customer_id: report?.customer?.id || null,
      contact_id: report?.contact?.id || null,
      project_id: report?.project?.id || null,
      folder_id: report?.folder?.id || null,
      order_date: report?.order_date
        ? String(report.order_date).substring(0, 10)
        : null,
      report_number: report?.report_number || "",
      description: report?.description || "",
      internal_description: report?.internal_description || "",
      delivery_address_id: report?.delivery_address?.id || null,
      system_id: report?.system?.id || null,
      useCustomerAddress: report?.useCustomerAddress || false,
      invoice_to_project_customer: report?.invoice_to_project_customer || false,
      other_invoice_customer: report?.invoice_customer?.id ? true : false,
      invoice_customer_id: report?.invoice_customer?.id || null,
      cost_included_in_project: report?.cost_included_in_project || false,
      sales_income_included_in_project:
        report?.sales_income_included_in_project || false,
      do_not_expect_income: report?.do_not_expect_income || false,
      documents: report?.documents || [],
      submit: null,
    }),
    [report]
  );

  const economyToggles = [
    {
      key: "invoice_to_project_customer",
      title: t("report.edit.info.invoice_to_project_customer"),
      desc: t("report.edit.info.invoice_to_project_customer_desc"),
    },
    {
      key: "other_invoice_customer",
      title: t("report.edit.info.other_invoice_customer"),
      desc: t("report.edit.info.other_invoice_customer_desc"),
    },
    {
      key: "cost_included_in_project",
      title: t("report.edit.info.cost_included_in_project"),
      desc: t("report.edit.info.cost_included_in_project_desc"),
    },
    {
      key: "sales_income_included_in_project",
      title: t("report.edit.info.sales_income_included_in_project"),
      desc: t("report.edit.info.sales_income_included_in_project_desc"),
    },
    {
      key: "do_not_expect_income",
      title: t("report.edit.info.do_not_expect_income"),
      desc: t("report.edit.info.do_not_expect_income_desc"),
    },
  ];

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object({
        customer_id: Yup.string().nullable().required(t("customer.required")),
        report_type: Yup.string()
          .nullable()
          .required(t("report.type.required")),
        project_id: Yup.string().nullable().required(t("project.required")),
      })}
      onSubmit={async (values, helpers) => {
        if (blockedRef.current) return;
        let data;
        try {
          helpers.setStatus(null);
          data = await dispatch(updateReportAsync(values)).unwrap();
        } catch (err) {
          const problem = normalizeError(err);
          blockedRef.current = problem.outcome === 'unknown' || problem.kind === 'conflict';
          helpers.setStatus({ error: problem.safeMessage });
          return;
        } finally { helpers.setSubmitting(false); }
        onRefresh?.(data);
        onClose?.();
      }}
    >
      {(formik) => {
        const fieldError = (name) => (formik.touched[name] ? formik.errors[name] : undefined);
        const selectedType = reportTypes.find((row) => String(row.id) === String(formik.values.report_type))
          || (String(report?.report_type?.id) === String(formik.values.report_type) ? report.report_type : null);
        const postalTitle = String(customer?.id ?? "") === String(report?.customer?.id ?? "")
          ? report?.customer?.postal_address?.displayTitle
          : customer?.postal_address?.displayTitle;
        const changeCustomer = (event) => {
          if (formik.isSubmitting) return;
          const id = event.target.value;
          setCustomer(event.target.data || null);
          if (String(id) === String(formik.values.customer_id)) return;
          setContact(null);
          setSystem(null);
          setAddress(null);
          formik.setValues({
            ...formik.values,
            customer_id: id,
            customer_id_locked: Boolean(event.target.data?.profile_locked),
            contact_id: null,
            system_id: null,
            delivery_address_id: null,
            useCustomerAddress: false,
          });
        };
        return (
        <Form>
          <FooterSync
            registerFooter={registerFooter}
            state={{
              dirty: formik.dirty,
              saving: formik.isSubmitting,
              needsReview: Boolean(formik.status?.error),
              errorMessage: formik.status?.error,
              onSave: formik.submitForm,
            }}
          />
          <div className="rv2-section-title">
            {t("report.edit.info.heading")}
          </div>
          <p className="rv2-section-desc">{t("report.edit.info.subheading")}</p>

          <div className="rv2-frow rv2-fgroup rv2-selectors">
            <div>
              <ReportTypeSelector selected={report?.report_type} {...formik} />
            </div>
            <div>
              <EmployeeSelector
                name="executing_employee"
                includeHired
                label={t("report.edit.info.executor")}
                selected={report?.executing_employee}
                {...formik}
              />
            </div>
          </div>

          <div className="rv2-frow rv2-fgroup rv2-selectors">
            <div>
              <CustomerPicker
                required
                label={t("report.edit.info.customer")}
                value={customer}
                onChange={changeCustomer}
                disabled={formik.isSubmitting}
                error={fieldError("customer_id")}
              />
            </div>
            <div>
              <ContactPicker
                label={t("report.edit.info.contact")}
                value={contact}
                customer={customer?.id != null ? customer : formik.values.customer_id ? { id: formik.values.customer_id } : null}
                disabled={formik.isSubmitting}
                onChange={(event) => {
                  if (formik.isSubmitting) return;
                  setContact(event.target.data);
                  formik.setFieldValue("contact_id", event.target.value);
                }}
              />
            </div>
          </div>

          <div className="rv2-frow rv2-fgroup rv2-selectors">
            <div>
              <ProjectPicker
                required
                label={t("report.edit.info.project")}
                value={project}
                customerId={formik.values.customer_id}
                disabled={formik.isSubmitting}
                onParentObjectChange={setProject}
                onChange={(event) => {
                  if (formik.isSubmitting) return;
                  formik.setFieldValue("project_id", event.target.value);
                  formik.setFieldValue("folder_id", null);
                  setFolder(null);
                }}
                error={fieldError("project_id")}
              />
            </div>
            <div>
              <ProjectFolderPicker
                label={t("report.edit.info.folder")}
                value={folder}
                projectId={formik.values.project_id}
                disabled={formik.isSubmitting}
                onChange={(event) => {
                  if (formik.isSubmitting) return;
                  setFolder(event.object);
                  formik.setFieldValue("folder_id", event.target.value);
                }}
              />
            </div>
          </div>

          <div className="rv2-frow rv2-fgroup">
            <div>
              <label className="rv2-flabel">
                {t("report.edit.info.order_date")}
              </label>
              <input
                type="date"
                className="rv2-input"
                value={formik.values.order_date || ""}
                onChange={(e) =>
                  formik.setFieldValue("order_date", e.target.value)
                }
              />
            </div>
            <div>
              <label className="rv2-flabel">
                {t("report.edit.info.report_number")}
              </label>
              <input
                type="text"
                className="rv2-input"
                value={formik.values.report_number || ""}
                disabled
              />
            </div>
          </div>

          {/* Address & site */}
          <div className="rv2-tg-row rv2-tg-inline">
            <div className="rv2-tg-txt">
              <div className="rv2-t">
                {t("report.edit.info.use_customer_address")}
              </div>
              {postalTitle && (
                <div className="rv2-d">
                  {postalTitle}
                </div>
              )}
            </div>
            <button
              type="button"
              className={
                "rv2-switch" +
                (formik.values.useCustomerAddress ? " rv2-on" : "")
              }
              aria-pressed={!!formik.values.useCustomerAddress}
              onClick={() => {
                const next = !formik.values.useCustomerAddress;
                formik.setFieldValue("useCustomerAddress", next);
                if (next) {
                  formik.setFieldValue("delivery_address_id", null);
                  setAddress(null);
                }
              }}
            />
          </div>

          {!formik.values.useCustomerAddress && formik.values.customer_id && (
            <div className="rv2-fgroup rv2-selectors">
              <DeliveryAddressPicker
                label={t("report.edit.info.address")}
                value={formik.values.delivery_address_id ? address : null}
                customerId={formik.values.customer_id}
                disabled={formik.isSubmitting}
                onChange={(event) => {
                  if (formik.isSubmitting) return;
                  setAddress(event.target.data);
                  formik.setFieldValue("delivery_address_id", event.target.value);
                }}
              />
            </div>
          )}

          {selectedType?.system && (
            <div className="rv2-fgroup rv2-selectors">
              <SystemPicker
                label={t("report.edit.info.site")}
                value={formik.values.system_id ? system : null}
                customerId={formik.values.customer_id}
                disabled={formik.isSubmitting}
                onChange={(event) => {
                  if (formik.isSubmitting) return;
                  setSystem(event.target.data);
                  formik.setFieldValue("system_id", event.target.value);
                }}
              />
            </div>
          )}

          {/* Descriptions */}
          <div className="rv2-fgroup">
            <div className="rv2-flabel-row">
              <label className="rv2-flabel">
                {t("report.edit.info.description")}{" "}
                <span className="rv2-fhint">
                  · {t("report.edit.info.description_hint")}
                </span>
              </label>
              <TemplateDialogButton fieldName="description" {...formik} />
            </div>
            <QuillEditor name="description" rows={4} {...formik} onChange={(html, _delta, source) => { if (source === "user") formik.setFieldValue("description", html); }} />
          </div>

          <div className="rv2-fgroup">
            <div className="rv2-flabel-row">
              <label className="rv2-flabel">
                {t("report.edit.info.internal")}{" "}
                <span className="rv2-fhint">
                  · {t("report.edit.info.internal_hint")}
                </span>
              </label>
              <TemplateDialogButton
                fieldName="internal_description"
                {...formik}
              />
            </div>
            <QuillEditor name="internal_description" rows={3} {...formik} onChange={(html, _delta, source) => { if (source === "user") formik.setFieldValue("internal_description", html); }} />
          </div>

          {/* Documents */}
          {report?.id && (
            <div className="rv2-fgroup">
              <label className="rv2-flabel">
                {t("report.edit.info.documents")}
              </label>
              <div className="rv2-doc-row">
                <DocumentUploadSectionV5
                  multiple
                  key={`upload-${report?.id}`}
                  module="report_description"
                  moduleParam={report?.id}
                  parentCallback={(data) =>
                    formik.setFieldValue("documents", data)
                  }
                  buttonStyle="compact"
                />
                <button
                  type="button"
                  className="rv2-doc-view"
                  onClick={() => setDocumentsDialogOpen(true)}
                  title={t("report.edit.info.view_documents")}
                >
                  <DocumentIcon aria-hidden />
                  {report?.documents?.length > 0 && (
                    <span className="rv2-doc-badge">
                      {report.documents.length}
                    </span>
                  )}
                </button>
              </div>
              <DocumentsDialog
                open={documentsDialogOpen}
                onClose={() => setDocumentsDialogOpen(false)}
                module="report_description"
                moduleParam={report?.id}
                onRefresh={onRefresh}
              />
            </div>
          )}

          {/* Economy & invoicing */}
          <div className="rv2-section-title" style={{ marginTop: 20 }}>
            {t("report.edit.info.economy")}
          </div>
          <div className="rv2-tg-card">
            {economyToggles.map((tg) => (
              <div className="rv2-tg-row" key={tg.key}>
                <div className="rv2-tg-txt">
                  <div className="rv2-t">{tg.title}</div>
                  {tg.desc && <div className="rv2-d">{tg.desc}</div>}
                </div>
                <button
                  type="button"
                  className={
                    "rv2-switch" + (formik.values[tg.key] ? " rv2-on" : "")
                  }
                  aria-pressed={!!formik.values[tg.key]}
                  onClick={() => {
                    const next = !formik.values[tg.key];
                    formik.setFieldValue(tg.key, next);
                    if (tg.key === "other_invoice_customer" && !next) {
                      setInvoiceCustomer(null);
                      formik.setFieldValue("invoice_customer_id", null);
                    }
                  }}
                />
              </div>
            ))}
          </div>

          {formik.values.other_invoice_customer && (
            <div className="rv2-fgroup rv2-selectors" style={{ marginTop: 14 }}>
              <CustomerPicker
                name="invoice_customer_id"
                label={t("report.edit.info.invoice_customer")}
                value={invoiceCustomer}
                setFieldValue={formik.setFieldValue}
                onChange={(event) => setInvoiceCustomer(event.target.data)}
                disabled={formik.isSubmitting}
                allowCreate={false}
              />
            </div>
          )}
        </Form>
        );
      }}
    </Formik>
  );
}

/* ----------------------------------------------------------------------- */
/* Sjekkliste — embeds the production checklist editor, saves via report     */
/* ----------------------------------------------------------------------- */
function ChecklistEditor({ report, onRefresh, onClose, registerFooter }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const itemsRef = useRef(report?.checklist?.items || []);
  const baselineSignatureRef = useRef(
    checklistItemsSignature(report?.checklist?.items)
  );
  const [itemCount, setItemCount] = useState(
    report?.checklist?.items?.length || 0
  );
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const versionRef = useRef(report?.edit_version);
  const blockedRef = useRef(false);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [deletePromptOpen, setDeletePromptOpen] = useState(false);
  const [lastChecklistId, setLastChecklistId] = useState(
    report?.checklist?.id || null
  );

  const canDeleteChecklist = useUserHasAccess({
    role: "report",
    action: "delete_checklist",
  });

  // Re-seed when the underlying checklist is created or deleted.
  if ((report?.checklist?.id || null) !== lastChecklistId) {
    versionRef.current = report?.edit_version;
    setLastChecklistId(report?.checklist?.id || null);
    const nextItems = report?.checklist?.items || [];
    itemsRef.current = nextItems;
    baselineSignatureRef.current = checklistItemsSignature(nextItems);
    setItemCount(nextItems.length);
    setDirty(false);
  }

  const handleItemsChange = useCallback((data) => {
    itemsRef.current = data;
    setItemCount(data.length);
    setDirty(
      baselineSignatureRef.current !== checklistItemsSignature(data)
    );
  }, []);

  const handleChecklistSelect = useCallback(
    async (config) => {
      try {
        const payload = {
          report: {
            id: report?.id,
          },
          fields: "id,checklist,edit_version",
        };

        if (config) {
          if (config.mode === "type") {
            payload.report.checklist_type_id = config.checklist_type_id;
            if (
              config.selected_item_ids &&
              config.selected_item_ids.length > 0
            ) {
              payload.report.selected_item_ids = config.selected_item_ids;
            }
          } else if (config.mode === "main_report" && config.items) {
            payload.report.copy_from_checklist_items = config.items;
          }
          // mode === "new" creates an empty checklist (no extra params needed)
        }

        const res = await createReportChecklist(payload);

        if (res.data.id && res.data.checklist) {
          toast.success(t("report.edit.checklist.created"));

          // Fetch the full checklist (with items) so the editor renders rows.
          let checklist = res.data.checklist;
          try {
            const full = await fetchReportChecklist(report.id, {
              fields:
                "id,title,description,status,is_items_editable,items.*,checklist_type.id,checklist_type.displayTitle",
            });
            const checklistData = full?.data || full;
            if (checklistData && checklistData.id) {
              checklist = {
                ...checklistData,
                items: Array.isArray(checklistData.items)
                  ? checklistData.items
                  : [],
              };
            }
          } catch (e) {
            // Fall back to the minimal checklist from the create response.
          }

          onRefresh?.({ id: report.id, checklist, edit_version: res.data.edit_version });
          setSelectorOpen(false);
        }
      } catch (err) {
        toast.error(t("report.edit.checklist.create_error"));
        console.error("Error creating checklist:", err);
      }
    },
    [report?.id, onRefresh, t]
  );

  const deleteChecklist = useCallback(async () => {
    try {
      const res = await deleteReportChecklist({ id: report?.id });
      if (!res?.data?.id) {
        toast.error(t("report.edit.checklist.delete_error"));
        return;
      }
      toast.success(t("report.edit.checklist.deleted"));
      setDeletePromptOpen(false);
      onRefresh?.({ id: report.id, checklist: null, edit_version: res.data.edit_version });
    } catch (err) {
      toast.error(t("report.edit.checklist.delete_error"));
      console.error("Error deleting checklist:", err);
    }
  }, [report?.id, onRefresh, t]);

  const save = useCallback(async () => {
    if (saving || blockedRef.current) return;
    const itemsToSave = itemsRef.current;
    setSaving(true);
    setSaveError(null);
    let data;
    try {
      data = await dispatch(updateReportAsync({
        id: report.id,
        edit_version: versionRef.current,
        checklist: { ...report.checklist, items: itemsToSave },
      })).unwrap();
    } catch (error) {
      const problem = normalizeError(error);
      blockedRef.current = problem.outcome === 'unknown' || problem.kind === 'conflict';
      setSaveError(problem.safeMessage);
      return;
    } finally { setSaving(false); }
    versionRef.current = data.edit_version;
    setDirty(false);
    baselineSignatureRef.current = checklistItemsSignature(itemsToSave);
    onRefresh?.({ ...data, checklist: { ...report.checklist, items: itemsToSave } });
    onClose?.();
  }, [dispatch, report, onRefresh, onClose, saving]);

  if (!report?.checklist?.id) {
    return (
      <div>
        <FooterSync registerFooter={registerFooter} state={{ embedded: true }} />
        <div className="rv2-section-title">
          {t("report.edit.section.checklist")}
        </div>
        <p className="rv2-section-desc">
          {t("report.edit.checklist.none")}
        </p>
        <div className="rv2-frow rv2-fgroup" style={{ gap: 10 }}>
          <button
            type="button"
            className="rv2-btn rv2-primary"
            onClick={() => setSelectorOpen(true)}
          >
            {t("report.edit.checklist.select")}
          </button>
          <button
            type="button"
            className="rv2-btn"
            onClick={() =>
              navigate(`/dashboard/service/reports/${report?.id}/checklist`)
            }
          >
            {t("report.edit.checklist.open")}
          </button>
        </div>

        <EnhancedChecklistSelector
          open={selectorOpen}
          onClose={() => setSelectorOpen(false)}
          onSelect={handleChecklistSelect}
          mainReportChecklist={report?.sequelMainReport?.checklist || null}
          mainReport={report?.sequelMainReport || null}
          showTitleField={false}
        />
      </div>
    );
  }

  return (
    <div>
      <FooterSync
        registerFooter={registerFooter}
        state={{ dirty, saving, onSave: save, needsReview: Boolean(saveError), errorMessage: saveError }}
      />
      <div className="rv2-section-title">
        {t("report.edit.section.checklist")} · {itemCount}
      </div>
      <p className="rv2-section-desc">{t("report.edit.checklist.hint")}</p>

      {canDeleteChecklist && (
        <div
          className="rv2-fgroup"
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <button
            type="button"
            className="rv2-btn rv2-danger"
            onClick={() => setDeletePromptOpen(true)}
          >
            <TrashIcon aria-hidden />
            {t("report.edit.checklist.delete")}
          </button>
        </div>
      )}

      <div className="rv2-embed">
        <ChecklistItemLines
          key={report?.checklist?.id}
          checklist={report?.checklist}
          items={report?.checklist?.items}
          editable
          drag
          addRow
          deleteRow
          deviation={false}
          type="edit"
          disabled={
            report?.checklist?.is_items_editable !== undefined &&
            !report?.checklist?.is_items_editable
          }
          parentCallback={handleItemsChange}
        />
      </div>

      <SimplePrompt
        title={t("report.edit.checklist.delete")}
        description={t("report.edit.checklist.delete_confirm")}
        open={deletePromptOpen}
        onClose={() => setDeletePromptOpen(false)}
        onAccept={deleteChecklist}
      />
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Prislinjer — embeds the production price-line editor (self-saving)        */
/* ----------------------------------------------------------------------- */
function PriceEditor({ report, registerFooter }) {
  const { t } = useTranslation();
  return (
    <div>
      <FooterSync
        registerFooter={registerFooter}
        state={{ embedded: true, note: t("report.edit.autosaved") }}
      />
      <div className="rv2-section-title">{t("report.edit.section.price")}</div>
      <p className="rv2-section-desc">{t("report.edit.price.hint")}</p>
      <div className="rv2-embed">
        <ReportPriceLinesSection
          reportId={report?.id}
          project={report?.project}
        />
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Ansvarlige — embeds the production participants editor (self-saving)      */
/* ----------------------------------------------------------------------- */
function TeamEditor({ report, canEdit, onParticipantsChange, registerFooter }) {
  const { t } = useTranslation();
  return (
    <div>
      <FooterSync
        registerFooter={registerFooter}
        state={{ embedded: true, note: t("report.edit.autosaved") }}
      />
      <div className="rv2-section-title">{t("report.edit.section.team")}</div>
      <p className="rv2-section-desc">{t("report.edit.team.hint")}</p>
      <ReportAccessesSection
        reportId={report?.id}
        participants={report?.participants || []}
        onParticipantsChange={onParticipantsChange}
        canManage={canEdit}
        executingEmployeeId={report?.executing_employee?.id}
      />
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Status & innstillinger — wired to updateReportAsync                       */
/* ----------------------------------------------------------------------- */
const STATUS_CARDS = [
  { value: "UNDER_PROCESS", label: "report.edit.status.under_process", dot: "#E0A82E" },
  { value: "PROCESSED", label: "report.edit.status.processed", dot: "#18A65B" },
  { value: "CLOSED", label: "report.edit.status.closed", dot: "#67718A" },
];

function StatusEditor({ report, onRefresh, onClose, registerFooter }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [draft, setDraft] = useState(() => ({
    id: report?.id,
    edit_version: report?.edit_version,
    status: report?.processStatus
      ? String(report.processStatus).toUpperCase()
      : "UNDER_PROCESS",
    finished: !!report?.finished,
    finished_date: report?.finished_date
      ? String(report.finished_date).substring(0, 10)
      : null,
    invoiced: !!report?.invoiced,
    delete: false,
  }));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const blockedRef = useRef(false);

  const patch = (p) => {
    setDraft((d) => ({ ...d, ...p }));
    setDirty(true);
  };

  const save = useCallback(async () => {
    if (saving || blockedRef.current) return;
    setSaving(true);
    setSaveError(null);
    let data;
    try {
      data = await dispatch(updateReportAsync(draft)).unwrap();
    } catch (error) {
      const problem = normalizeError(error);
      blockedRef.current = problem.outcome === 'unknown' || problem.kind === 'conflict';
      setSaveError(problem.safeMessage);
      return;
    } finally { setSaving(false); }
    setDirty(false);
    onRefresh?.(data);
    onClose?.();
  }, [dispatch, draft, onRefresh, onClose, saving]);

  const toggles = [
    {
      key: "finished",
      title: t("report.edit.status.finished"),
      desc: t("report.edit.status.finished_desc"),
    },
    {
      key: "invoiced",
      title: t("report.edit.status.invoiced"),
      desc: t("report.edit.status.invoiced_desc"),
    },
  ];

  return (
    <div>
      <FooterSync
        registerFooter={registerFooter}
        state={{ dirty, saving, onSave: save, needsReview: Boolean(saveError), errorMessage: saveError }}
      />
      <div className="rv2-section-title">{t("report.edit.status.heading")}</div>
      <p className="rv2-section-desc">{t("report.edit.status.subheading")}</p>
      <div className="rv2-statusgrid">
        {STATUS_CARDS.map((c) => (
          <button
            key={c.value}
            type="button"
            className={"rv2-statuscard" + (draft.status === c.value ? " rv2-sel" : "")}
            onClick={() => patch({ status: c.value })}
          >
            <span className="rv2-dot" style={{ background: c.dot }} />
            {t(c.label)}
          </button>
        ))}
      </div>

      {draft.finished && (
        <div className="rv2-fgroup" style={{ marginTop: 18 }}>
          <label className="rv2-flabel">
            {t("report.edit.status.finished_date")}
          </label>
          <input
            type="date"
            className="rv2-input"
            value={draft.finished_date || ""}
            onChange={(e) => patch({ finished_date: e.target.value })}
          />
        </div>
      )}

      <div className="rv2-section-title" style={{ marginTop: 20 }}>
        {t("report.edit.status.settings")}
      </div>
      <div className="rv2-tg-card">
        {toggles.map((tg) => (
          <div className="rv2-tg-row" key={tg.key}>
            <div className="rv2-tg-txt">
              <div className="rv2-t">{tg.title}</div>
              <div className="rv2-d">{tg.desc}</div>
            </div>
            <button
              type="button"
              className={"rv2-switch" + (draft[tg.key] ? " rv2-on" : "")}
              aria-pressed={!!draft[tg.key]}
              onClick={() => patch({ [tg.key]: !draft[tg.key] })}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Footer registration helper                                               */
/* ----------------------------------------------------------------------- */
function FooterSync({ registerFooter, state }) {
  useEffect(() => {
    registerFooter(state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });
  return null;
}

/* ----------------------------------------------------------------------- */
/* Drawer shell                                                             */
/* ----------------------------------------------------------------------- */
export function ReportEditDrawer({
  prepareRemoteRefresh,
  open,
  section,
  report,
  canEdit = true,
  onClose,
  onRefresh,
  onParticipantsChange,
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(section || "info");
  const [confirmClose, setConfirmClose] = useState(false);
  const [remoteEpoch, setRemoteEpoch] = useState(0);

  const footerRef = useRef({});
  const [, setFooterTick] = useState(0);
  const registerFooter = useCallback((state) => {
    const prev = footerRef.current;
    footerRef.current = state || {};
    if (
      prev.dirty !== footerRef.current.dirty ||
      prev.saving !== footerRef.current.saving ||
      prev.needsReview !== footerRef.current.needsReview ||
      prev.errorMessage !== footerRef.current.errorMessage ||
      prev.embedded !== footerRef.current.embedded ||
      prev.note !== footerRef.current.note
    ) {
      setFooterTick((x) => x + 1);
    }
  }, []);

  // True when the active section has explicit, unsaved edits (auto-saving
  // sections set `embedded` and never block closing).
  const isDirty = useCallback(() => {
    const f = footerRef.current;
    return !!f?.dirty && !f?.embedded;
  }, []);

  const requestClose = useCallback(() => {
    if (isDirty()) setConfirmClose(true);
    else onClose?.();
  }, [isDirty, onClose]);

  const discardAndClose = useCallback(() => {
    setConfirmClose(false);
    footerRef.current = {};
    onClose?.();
  }, [onClose]);

  const saveAndClose = useCallback(() => {
    setConfirmClose(false);
    // Section save handlers close the drawer on success.
    footerRef.current?.onSave?.();
  }, []);

  useEffect(() => {
    if (open) {
      setTab(section || "info");
      footerRef.current = {};
      setConfirmClose(false);
    }
  }, [open, section]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key !== "Escape") return;
      if (confirmClose) setConfirmClose(false);
      else requestClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, confirmClose, requestClose]);

  // Warn before a full browser unload/navigation while edits are pending.
  useEffect(() => {
    if (!open) return;
    const onBeforeUnload = (e) => {
      if (isDirty()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [open, isDirty]);

  const changeTab = (id) => {
    footerRef.current = {};
    setTab(id);
  };

  const subline = useMemo(() => {
    const active = REPORT_EDIT_SECTIONS.find((s) => s.id === tab);
    return active ? t(active.label) : "";
  }, [tab, t]);

  if (!open || typeof document === "undefined") return null;

  const footer = footerRef.current;

  return createPortal(
    <div className="rv2-scope">
      <div className="rv2-scrim" onClick={requestClose} aria-hidden />
      <div
        className="rv2-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={t("report.edit.menu_title")}
      >
        <div className="rv2-drawer-head">
          <div className="rv2-dh-top">
            <div className="rv2-dh-ic">
              <PencilIcon aria-hidden />
            </div>
            <div className="rv2-dh-t">
              <h2>{t("report.edit.menu_title")}</h2>
              <div className="rv2-dh-sub">
                {report?.report_number ? `${report.report_number} · ` : ""}
                {subline}
              </div>
            </div>
            <button
              type="button"
              className="rv2-drawer-close"
              onClick={requestClose}
              aria-label={t("common.close")}
            >
              <XMarkIcon aria-hidden />
            </button>
          </div>
          <div className="rv2-dtabs">
            {REPORT_EDIT_SECTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                className={"rv2-dtab" + (tab === s.id ? " rv2-active" : "")}
                onClick={() => changeTab(s.id)}
              >
                <s.icon aria-hidden />
                {t(s.label)}
              </button>
            ))}
          </div>
        </div>

        <RecordCollaborationPanel report={report} section={tab} editing={Boolean(footer.dirty)} enabled={open} needsReview={Boolean(footer.needsReview)} errorMessage={footer.errorMessage}
          saving={Boolean(footer.saving) || Boolean(footer.embedded) || !["info", "status"].includes(tab)}
          canRefresh={() => !footerRef.current.dirty && !footerRef.current.saving && !footerRef.current.needsReview && !footerRef.current.embedded}
          onRefresh={prepareRemoteRefresh ? async () => {
            const snapshot = await prepareRemoteRefresh();
            return { apply: () => { snapshot.apply(); setRemoteEpoch(n => n + 1); } };
          } : undefined}
          onReload={() => {
            // The collaboration dialog already confirmed discarding this draft.
            // Clear only this editor's guard before the synchronous reload.
            footerRef.current = {};
            window.location.reload();
          }} />
        <div className="rv2-drawer-body">
          {tab === "info" && (
            <InfoEditor key={`info:${remoteEpoch}`}
              report={report}
              onRefresh={onRefresh}
              onClose={onClose}
              registerFooter={registerFooter}
            />
          )}
          {tab === "checklist" && (
            <ChecklistEditor key={`checklist:${remoteEpoch}`}
              report={report}
              onRefresh={onRefresh}
              onClose={onClose}
              registerFooter={registerFooter}
            />
          )}
          {tab === "price" && (
            <PriceEditor report={report} registerFooter={registerFooter} />
          )}
          {tab === "team" && (
            <TeamEditor
              report={report}
              canEdit={canEdit}
              onParticipantsChange={onParticipantsChange}
              registerFooter={registerFooter}
            />
          )}
          {tab === "status" && (
            <StatusEditor key={`status:${remoteEpoch}`}
              report={report}
              onRefresh={onRefresh}
              onClose={onClose}
              registerFooter={registerFooter}
            />
          )}
        </div>

        <div className="rv2-drawer-foot">
          {footer.embedded ? (
            <>
              <span className="rv2-foot-ok">
                {footer.note || t("report.edit.autosaved")}
              </span>
              <span className="rv2-foot-spacer" />
              <button type="button" className="rv2-btn" onClick={requestClose}>
                {t("common.close")}
              </button>
            </>
          ) : (
            <>
              {footer.dirty ? (
                <span className="rv2-dirty">
                  <i />
                  {t("report.edit.unsaved")}
                </span>
              ) : (
                <span className="rv2-foot-ok">{t("report.edit.all_saved")}</span>
              )}
              <span className="rv2-foot-spacer" />
              <button type="button" className="rv2-btn" onClick={requestClose}>
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="rv2-btn rv2-primary"
                disabled={!footer.dirty || footer.saving}
                onClick={() => footer.onSave?.()}
              >
                <CheckIcon aria-hidden />
                {t("common.save")}
              </button>
            </>
          )}
        </div>
      </div>

      {confirmClose && (
        <div className="rv2-confirm-scrim" role="dialog" aria-modal="true">
          <div className="rv2-confirm">
            <h3 className="rv2-confirm-title">
              {t("report.edit.confirm_close.title")}
            </h3>
            <p className="rv2-confirm-text">
              {t("report.edit.confirm_close.text")}
            </p>
            <div className="rv2-confirm-actions">
              <button
                type="button"
                className="rv2-btn"
                onClick={() => setConfirmClose(false)}
              >
                {t("report.edit.confirm_close.keep_editing")}
              </button>
              <button
                type="button"
                className="rv2-btn rv2-danger"
                onClick={discardAndClose}
              >
                {t("report.edit.confirm_close.discard")}
              </button>
              <button
                type="button"
                className="rv2-btn rv2-primary"
                onClick={saveAndClose}
              >
                <CheckIcon aria-hidden />
                {t("report.edit.confirm_close.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

export default ReportEditDrawer;
